/**
 * ScoringRidgePlot Component
 *
 * 538-inspired ridge plot showing how score distributions change
 * across handicap brackets. Each row is a density curve for a
 * handicap level, stacked vertically from scratch (top) to 36 (bottom).
 */

import { useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../../../../components/graphs/useChartDimensions';
import { ChartCanvas } from '../../../../components/graphs/basicGraph';
import { simulateMultipleRounds } from '../utility/golfSimulation';
import { generateCourseConfig, COURSE_PRESETS } from '../utility/golfConfig';

const HANDICAP_BRACKETS = [0, 5, 10, 15, 20, 25, 30, 36];

const BRACKET_COLOURS = {
    0:  '#065F46',  // Dark green
    5:  '#047857',
    10: '#059669',
    15: '#10B981',
    20: '#F59E0B',
    25: '#F97316',
    30: '#EF4444',
    36: '#DC2626',  // Red
};

const BRACKET_LABELS = {
    0: 'Scratch',
    5: '5',
    10: '10',
    15: '15',
    20: '20',
    25: '25',
    30: '30',
    36: '36',
};

/**
 * Generate score distributions for each handicap bracket
 */
const generateDistributions = (course, consistency = 0.25) => {
    return HANDICAP_BRACKETS.map(hcp => {
        const results = simulateMultipleRounds(hcp, course, 300, consistency);
        const par = course.par;

        // Convert to relative-to-par scores
        const relativeToPar = results.scores.map(s => s - par);

        return {
            handicap: hcp,
            scores: relativeToPar,
            avg: results.avgScore - par
        };
    });
};

/**
 * Compute kernel density estimate for smooth curves
 */
const kernelDensity = (values, bandwidth, xDomain) => {
    const step = 0.5;
    const points = [];
    for (let x = xDomain[0]; x <= xDomain[1]; x += step) {
        let density = 0;
        for (const v of values) {
            const u = (x - v) / bandwidth;
            density += Math.exp(-0.5 * u * u) / (bandwidth * Math.sqrt(2 * Math.PI));
        }
        density /= values.length;
        points.push({ x, y: density });
    }
    return points;
};

export const ScoringRidgePlot = ({ course, consistency = 0.25 }) => {
    const [ref, dimensions] = useChartDimensions({
        marginTop: 20,
        marginRight: 30,
        marginBottom: 35,
        marginLeft: 55,
        height: 400
    });

    const distributions = useMemo(() => {
        return generateDistributions(course, consistency);
    }, [course, consistency]);

    useEffect(() => {
        if (!distributions.length || !dimensions.boundedWidth) return;

        const bounds = d3.select(ref.current).select('.ridge-bounds');
        bounds.selectAll('*').remove();

        // Find global x domain
        const allScores = distributions.flatMap(d => d.scores);
        const xMin = Math.floor(Math.min(...allScores)) - 2;
        const xMax = Math.ceil(Math.max(...allScores)) + 2;

        const x = d3.scaleLinear()
            .domain([xMin, xMax])
            .range([0, dimensions.boundedWidth]);

        // Row height for each bracket
        const rowHeight = dimensions.boundedHeight / HANDICAP_BRACKETS.length;
        const overlap = 1.4; // How much rows overlap

        // Compute density curves
        const densities = distributions.map(dist => ({
            ...dist,
            density: kernelDensity(dist.scores, 1.2, [xMin, xMax])
        }));

        // Find max density for scaling
        const maxDensity = Math.max(...densities.flatMap(d => d.density.map(p => p.y)));

        const yHeight = d3.scaleLinear()
            .domain([0, maxDensity])
            .range([0, rowHeight * overlap]);

        // Draw each ridge (back to front — highest handicap first so lower handicaps overlay)
        const ridges = [...densities].reverse();

        ridges.forEach((dist) => {
            const bracketIndex = HANDICAP_BRACKETS.indexOf(dist.handicap);
            const baseY = bracketIndex * rowHeight;
            const colour = BRACKET_COLOURS[dist.handicap];

            const area = d3.area()
                .x(d => x(d.x))
                .y0(baseY + rowHeight)
                .y1(d => baseY + rowHeight - yHeight(d.y))
                .curve(d3.curveBasis);

            const line = d3.line()
                .x(d => x(d.x))
                .y(d => baseY + rowHeight - yHeight(d.y))
                .curve(d3.curveBasis);

            // Fill
            bounds.append('path')
                .datum(dist.density)
                .attr('d', area)
                .attr('fill', colour)
                .attr('fill-opacity', 0.35);

            // Stroke
            bounds.append('path')
                .datum(dist.density)
                .attr('d', line)
                .attr('fill', 'none')
                .attr('stroke', colour)
                .attr('stroke-width', 1.5);

            // Label on left
            bounds.append('text')
                .attr('x', -8)
                .attr('y', baseY + rowHeight - 2)
                .attr('text-anchor', 'end')
                .attr('fill', colour)
                .attr('font-size', '11px')
                .attr('font-weight', '600')
                .text(BRACKET_LABELS[dist.handicap]);
        });

        // Par line at 0
        if (xMin <= 0 && xMax >= 0) {
            bounds.append('line')
                .attr('x1', x(0))
                .attr('x2', x(0))
                .attr('y1', 0)
                .attr('y2', dimensions.boundedHeight)
                .attr('stroke', '#9CA3AF')
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '4,3');

            bounds.append('text')
                .attr('x', x(0))
                .attr('y', dimensions.boundedHeight + 25)
                .attr('text-anchor', 'middle')
                .attr('fill', '#6B7280')
                .attr('font-size', '10px')
                .attr('font-weight', '600')
                .text('Par');
        }

        // X axis
        const xAxis = d3.axisBottom(x)
            .ticks(Math.min(xMax - xMin, 15))
            .tickFormat(d => d > 0 ? `+${d}` : d === 0 ? 'E' : d);

        bounds.append('g')
            .attr('transform', `translate(0,${dimensions.boundedHeight})`)
            .call(xAxis)
            .selectAll('text')
            .attr('font-size', '10px');

        // Y axis label
        bounds.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -dimensions.boundedHeight / 2)
            .attr('y', -42)
            .attr('text-anchor', 'middle')
            .attr('fill', '#6B7280')
            .attr('font-size', '11px')
            .text('Handicap');

    }, [distributions, dimensions, ref]);

    return (
        <div className="w-full" ref={ref}>
            <ChartCanvas dimensions={dimensions}>
                <g className="ridge-bounds" />
            </ChartCanvas>
        </div>
    );
};

export default ScoringRidgePlot;
